import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from '@mui/material';
import type { Paginated } from '@fdt/shared';
import type { SvgIconComponent } from '@mui/icons-material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { errorKey } from '../api/client';
import { EmptyState } from './EmptyState';
import { PageHeader } from './PageHeader';
import type { Tone } from './IconTile';

export interface Column<T> {
  /** Field name sent as `sortBy`; omit `sortable` for display-only columns. */
  id: string;
  label: string;
  render: (row: T) => ReactNode;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
}

interface DataTableProps<T> {
  title: string;
  /** Repeats the sidebar's icon for this section at the top of the page. */
  icon?: SvgIconComponent;
  tone?: Tone;
  columns: Column<T>[];
  /** The `list` query from `useCrud`. */
  query: {
    data?: Paginated<T>;
    isPending: boolean;
    isFetching: boolean;
    isError: boolean;
    error: unknown;
  };
  search: string;
  onSearchChange: (value: string) => void;
  sortBy?: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
  page: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  rowKey: (row: T) => string;
  onCreate?: () => void;
  createLabel?: string;
  /** Makes the whole row clickable, for tables that open a detail screen. */
  onRowClick?: (row: T) => void;
  /** Rendered between the search box and the table, e.g. filter selects. */
  toolbar?: ReactNode;
}

export function DataTable<T>({
  title,
  icon,
  tone,
  columns,
  query,
  search,
  onSearchChange,
  sortBy,
  sortDir,
  onSort,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  rowKey,
  onCreate,
  createLabel,
  toolbar,
  onRowClick,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const rows = query.data?.items ?? [];

  return (
    <Stack spacing={2}>
      <PageHeader
        icon={icon}
        tone={tone}
        title={title}
        // The count comes from the server's `total`, so it is the size of the
        // whole result rather than of the page being looked at. Hidden while
        // the first page loads, because "0" that becomes "148" reads as a
        // list that was empty and then was not.
        subtitle={
          query.data ? t('common.rowCount', { count: query.data.total }) : undefined
        }
        actions={
          onCreate ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate}>
              {createLabel ?? t('common.create')}
            </Button>
          ) : undefined
        }
      />

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t('common.search')}
          sx={{ minWidth: 260 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        {toolbar}
        {/* Refetches are shown without replacing the table, so the page does not jump. */}
        {query.isFetching && !query.isPending && <CircularProgress size={18} />}
      </Stack>

      {query.isError && <Alert severity="error">{t(errorKey(query.error))}</Alert>}

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{ width: column.width, fontWeight: 600, whiteSpace: 'nowrap' }}
                    sortDirection={sortBy === column.id ? sortDir : false}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={sortBy === column.id}
                        direction={sortBy === column.id ? sortDir : 'asc'}
                        onClick={() => onSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isPending && (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <Box sx={{ py: 4, display: 'grid', placeItems: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {!query.isPending && rows.length === 0 && (
                <TableRow sx={{ '&:hover > .MuiTableCell-body': { backgroundColor: 'transparent' } }}>
                  <TableCell colSpan={columns.length} sx={{ borderBottom: 0 }}>
                    {/* The search box is above this table, so an empty result
                        has two quite different causes and the message says
                        which one it is: nothing here yet, or nothing matched. */}
                    {search.trim() ? (
                      <EmptyState
                        icon={SearchIcon}
                        title={t('common.noResults')}
                        body={t('common.noResultsHint', { query: search.trim() })}
                      />
                    ) : (
                      <EmptyState icon={icon} title={t('common.noData')} />
                    )}
                  </TableCell>
                </TableRow>
              )}

              {rows.map((row) => (
                <TableRow
                  key={rowKey(row)}
                  hover
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align}>
                      {column.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={query.data?.total ?? 0}
          page={Math.max(0, page - 1)}
          onPageChange={(_, nextPage) => onPageChange(nextPage + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => {
            onPageSizeChange(Number(event.target.value));
            onPageChange(1);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t('common.rowsPerPage')}
          // MUI's default is English ("1-20 of 20"), which would show untranslated.
          labelDisplayedRows={({ from, to, count }) =>
            t('common.displayedRows', { from, to, count })
          }
          getItemAriaLabel={(type) => t(`common.pagination.${type}`)}
        />
      </Paper>
    </Stack>
  );
}
